import {
  create,
  get,
  parseCreationOptionsFromJSON,
  parseRequestOptionsFromJSON,
  AuthenticationPublicKeyCredential,
  RegistrationPublicKeyCredential
} from '@github/webauthn-json/browser-ponyfill';
import {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON
} from '@github/webauthn-json/src/webauthn-json/basic/json';
import axios, { AxiosRequestConfig } from 'axios';

import { getCsrfToken } from 'utils/auth';
import { API_ENDPOINTS } from 'utils/constants';
import { RedirectData } from 'utils/types';

class PasskeysController {
  private readonly config: AxiosRequestConfig;

  private readonly authAbortController?: AbortController;

  constructor() {
    this.config = {
      headers: {
        accept: 'application/json',
        'x-csrf-token': getCsrfToken()
      }
    };
    if (typeof window.AbortController !== 'undefined') {
      this.authAbortController = new AbortController();
      this.authAbortController.signal.onabort = () => true;
    }
  }

  private async _getPasskeyCreateOptions() {
    const { data } = await axios.get<PublicKeyCredentialCreationOptionsJSON>(
      API_ENDPOINTS.PASSKEY_CREATE_OPTIONS,
      this.config
    );
    return data;
  }

  private async _getPasskeyRequestOptions() {
    const { data } = await axios.get<PublicKeyCredentialRequestOptionsJSON>(
      API_ENDPOINTS.PASSKEY_REQUEST_OPTIONS,
      this.config
    );
    return data;
  }

  private async _verifyAndCreatePasskey(
    credential: RegistrationPublicKeyCredential
  ) {
    const { data } = await axios.put<RedirectData>(
      API_ENDPOINTS.CREATE_PASSKEY,
      {
        body: JSON.stringify(credential)
      },
      this.config
    );
    return data;
  }

  private async _verifyAndLoginWithPasskey(
    credential: AuthenticationPublicKeyCredential
  ) {
    const { data } = await axios.post<RedirectData>(
      API_ENDPOINTS.VERIFY_PASSKEY,
      {
        body: JSON.stringify(credential)
      },
      this.config
    );
    return data;
  }

  private async _createPasskeyFromOptions(
    publicKeyData: PublicKeyCredentialCreationOptionsJSON
  ) {
    const options = parseCreationOptionsFromJSON({
      publicKey: publicKeyData,
      signal: this.authAbortController?.signal
    });
    return create(options);
  }

  private async _getPasskeyFromOptions(
    publicKeyData: PublicKeyCredentialRequestOptionsJSON
  ) {
    const options = parseRequestOptionsFromJSON({
      publicKey: publicKeyData,
      signal: this.authAbortController?.signal,
      mediation: 'optional'
    });
    return get(options);
  }

  public async createPasskey(): Promise<RedirectData> {
    const createOptions = await this._getPasskeyCreateOptions();
    const credential = await this._createPasskeyFromOptions(createOptions);
    return this._verifyAndCreatePasskey(credential);
  }

  public async loginWithExistingPasskey(): Promise<RedirectData> {
    if (
      window.PublicKeyCredential &&
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      PublicKeyCredential.isConditionalMediationAvailable
    ) {
      const isCMA =
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await PublicKeyCredential.isConditionalMediationAvailable();
      if (isCMA) {
        const requestOptions = await this._getPasskeyRequestOptions();
        const credential = await this._getPasskeyFromOptions(
          requestOptions
        ).catch(() => {
          throw new Error('rejected_or_timeout');
        });
        return this._verifyAndLoginWithPasskey(credential);
      }
      throw new Error('not_available');
    } else {
      throw new Error('not_available');
    }
  }
}

export const passkeysController = new PasskeysController();
